import { useCallback, useEffect, useRef, useState } from 'react';
import { getThumbnailDownloadSignedUrl } from 'store/files/services/getThumbnailDownloadSignedUrl.service';
import { fetchPrivateProjectBankImageThumbnail } from 'utils/fetchPrivateProjectBankImageThumbnail';
import {
  THUMBNAIL_URL_LIFETIME_MS,
  THUMBNAIL_URL_TTL_MS,
  getCachedUrl,
  invalidateUrl,
} from 'utils/thumbnailUrlCache';
import useIsMountedRef from 'utils/useIsMountedRef';
import { isVideoThumbnail } from 'scenes/ProjectBank/utils/bankItem';

const IDLE = { key: null, src: null, status: 'idle' };

/**
 * How long after the hook received a URL it is sure to be valid: the backend
 * signs for THUMBNAIL_URL_LIFETIME_MS, and the cache may hand a URL out up to
 * THUMBNAIL_URL_TTL_MS after it was signed. An element that fails to load a
 * URL it has held for longer (a lazy image, or a video that loads only on
 * screen) may simply have come too late for it.
 */
export const THUMBNAIL_URL_SURE_VALID_MS =
  THUMBNAIL_URL_LIFETIME_MS - THUMBNAIL_URL_TTL_MS;

// Whether this browser plays the .webm video thumbnails, decided once per
// page load. Older iOS Safari plays no WebM at all; there a video asks for
// no thumbnail and keeps its format glyph.
let webmPlayable;

/**
 * Whether the backend's .webm video thumbnails play in this browser.
 * @returns {boolean}
 */
export function canPlayWebmThumbnails() {
  if (webmPlayable === undefined) {
    try {
      webmPlayable =
        document.createElement('video').canPlayType('video/webm') !== '';
    } catch (error) {
      webmPlayable = false;
    }
  }
  return webmPlayable;
}

/**
 * Forgets the decision of canPlayWebmThumbnails (tests).
 */
export function resetWebmThumbnailSupport() {
  webmPlayable = undefined;
}

/**
 * Cache key of a BankItem thumbnail descriptor: `pub:<filePermissionId>` for
 * public files (images and videos alike: one file is never both),
 * `prv:<projectId>:<fileId>` for private ones; null when the item has no
 * thumbnail (folders, documents, private videos).
 * @param {?{source: string, filePermissionId?: string, projectId?: string, fileId?: string, media?: string}} thumbnail
 * @returns {?string}
 */
export function getThumbnailCacheKey(thumbnail) {
  if (!thumbnail) return null;
  if (thumbnail.source === 'private') {
    if (!thumbnail.projectId || !thumbnail.fileId) return null;
    return `prv:${thumbnail.projectId}:${thumbnail.fileId}`;
  }
  if (!thumbnail.filePermissionId) return null;
  return `pub:${thumbnail.filePermissionId}`;
}

// The public endpoint answers with the bare signed URL string, the private
// helper already unwraps `{ downloadUrl }`; both are normalised to a string
// (or null, which the cache rejects) so a changed response shape cannot end
// up as an <img src="[object Object]">.
const toUrlString = (value) => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const url = value.downloadUrl || value.signedUrl || value.url;
    return typeof url === 'string' ? url : null;
  }
  return null;
};

const fetchThumbnailUrl = (thumbnail) => {
  if (thumbnail.source === 'private') {
    return fetchPrivateProjectBankImageThumbnail({
      projectId: thumbnail.projectId,
      fileId: thumbnail.fileId,
    }).then(toUrlString);
  }
  return getThumbnailDownloadSignedUrl(thumbnail.filePermissionId).then(
    toUrlString
  );
};

/**
 * The signed thumbnail URL of a public file, through the same cache as the
 * cards and rows (`pub:<filePermissionId>`), for what shows a thumbnail
 * outside them: the pins of the gallery view's floorplan map. The gallery's
 * own cache keeps a URL for good, and the backend signs for five minutes.
 * @param {string} filePermissionId
 * @returns {Promise<string>} rejects when there is no URL to be had
 */
export function getPublicThumbnailUrl(filePermissionId) {
  const thumbnail = { source: 'public', filePermissionId };
  const key = getThumbnailCacheKey(thumbnail);
  if (!key) {
    return Promise.reject(new Error('getPublicThumbnailUrl: no file'));
  }
  return getCachedUrl(key, () => fetchThumbnailUrl(thumbnail));
}

/**
 * Resolves the signed thumbnail URL of an image BankItem, or of a public
 * video (the looping .webm, see toBankItem; none is asked for where
 * canPlayWebmThumbnails says no), through the shared thumbnailUrlCache (one
 * request per file, shared across cards and rows). Items without
 * `thumbnail` stay 'idle' and never request anything. The returned
 * `onError` is meant for `<img onError>` or `<video onError>`. A URL the
 * element has held long enough to have expired (see
 * THUMBNAIL_URL_SURE_VALID_MS) is asked for again, once per URL: 'loading',
 * then the fresh URL for a new element. Any other failure marks the URL
 * broken, evicts it from the cache and lets the card fall back to the format
 * icon. Either way only the entry that handed out the failed URL is evicted.
 * @param {Object} item BankItem
 * @returns {{src: ?string, status: 'idle'|'loading'|'ready'|'error', onError: Function}}
 */
export function useBankThumbnail(item) {
  const descriptor = item ? item.thumbnail : null;
  const thumbnail =
    isVideoThumbnail(descriptor) && !canPlayWebmThumbnails()
      ? null
      : descriptor;
  const key = getThumbnailCacheKey(thumbnail);
  const isMountedRef = useIsMountedRef();
  const [state, setState] = useState(() =>
    key ? { key, src: null, status: 'loading' } : IDLE
  );
  // Bumped to ask the cache again after a URL that may have expired failed.
  const [attempt, setAttempt] = useState(0);
  // The URL in hand and when it arrived, and the last URL asked for again.
  const receivedRef = useRef({ key: null, src: null, at: 0 });
  const retriedSrcRef = useRef(null);

  useEffect(() => {
    if (!key) {
      setState((prev) => (prev.key === null ? prev : IDLE));
      return undefined;
    }
    let cancelled = false;
    setState((prev) =>
      prev.key === key && prev.status === 'loading'
        ? prev
        : { key, src: null, status: 'loading' }
    );
    getCachedUrl(key, () => fetchThumbnailUrl(thumbnail))
      .then((url) => {
        if (cancelled || !isMountedRef.current) return;
        receivedRef.current = { key, src: url, at: Date.now() };
        setState({ key, src: url, status: 'ready' });
      })
      .catch(() => {
        if (cancelled || !isMountedRef.current) return;
        setState({ key, src: null, status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [key, attempt]);

  const onError = useCallback(() => {
    const received = receivedRef.current;
    const failedSrc = received.key === key ? received.src : null;
    // Only the entry that handed this URL out goes: a newer URL another card
    // has fetched for the same file meanwhile stays.
    if (key && failedSrc) invalidateUrl(key, failedSrc);
    if (!isMountedRef.current) return;
    // Held long enough to have expired: ask once more, a new signature
    // (shared through the cache) may be all it needs. A URL that fails
    // sooner is broken (no thumbnail rendered, a format the browser cannot
    // show), and so is the next one when it fails right away too.
    const mayHaveExpired =
      !!failedSrc &&
      failedSrc !== retriedSrcRef.current &&
      Date.now() - received.at >= THUMBNAIL_URL_SURE_VALID_MS;
    if (mayHaveExpired) {
      retriedSrcRef.current = failedSrc;
      setState({ key, src: null, status: 'loading' });
      setAttempt((count) => count + 1);
      return;
    }
    setState({ key, src: null, status: 'error' });
  }, [key]);

  const isCurrent = state.key === key;
  return {
    src: isCurrent && state.status === 'ready' ? state.src : null,
    status: isCurrent ? state.status : key ? 'loading' : 'idle',
    onError,
  };
}

export default useBankThumbnail;
